import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { 
    collection, 
    getDocs, 
    query, 
    where, 
    orderBy 
} from "firebase/firestore";
import { FaUser, FaCalendarAlt, FaMapMarkerAlt, FaRoute, FaClock, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import Layout from "../components/Layout";

export default function DsTravelReport() {
    const [dsList, setDsList] = useState([]);
    const [selectedDs, setSelectedDs] = useState("");
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [travelLog, setTravelLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [clientId, setClientId] = useState("");

    useEffect(() => {
        const cId = localStorage.getItem("clientId");
        if (cId) setClientId(cId);

        const fetchDs = async () => {
            try {
                const q = query(collection(db, "dsMaster"), where("clientId", "==", cId || "mdsons"));
                const snap = await getDocs(q);
                setDsList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (err) {
                console.error("Error fetching DS list:", err);
            }
        };
        fetchDs();
    }, []);

    const fetchTravelData = async () => {
        if (!selectedDs) return;
        setLoading(true);
        try {
            const [y, m, d] = selectedDate.split("-").map(Number);
            const start = new Date(y, m - 1, d, 0, 0, 0, 0);
            const end = new Date(y, m - 1, d, 23, 59, 59, 999);

            // Fetch orders for this DS on this day
            // We'll check both dsId and potentially other identifiers if needed
            const q = query(
                collection(db, "Outlet_Orders"),
                where("dsId", "==", selectedDs),
                where("createdAt", ">=", start),
                orderBy("createdAt", "asc")
            );
            const snap = await getDocs(q);
            
            const logs = snap.docs.map(doc => {
                const data = doc.data();
                const cAt = data.createdAt?.toDate();
                if (!cAt || cAt > end) return null; 
                return {
                    id: doc.id,
                    outletName: data.outletName,
                    outletId: data.outletId,
                    time: cAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    lat: data.lat,
                    long: data.long,
                    amount: data.totalAmount || 0,
                    status: data.status || "COMPLETED"
                };
            }).filter(Boolean);

            setTravelLog(logs);
        } catch (err) {
            console.error(err);
            alert("Error retrieving travel intelligence: " + (err.message || "Network timeout"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout>
            <div className="layout-content">
                {/* HEADER */}
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Operational Topology</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Audit salesman travel patterns, visit sequences, and location-tagged productivity.</p>
                    </div>
                </div>

                {/* FILTER CONTROLS */}
                <div className="premium-card" style={{ padding: 24, marginBottom: 32, display: "flex", gap: 24, alignItems: "flex-end", background: "var(--surface-hover)" }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>
                            SELECT SALES EXECUTIVE
                        </label>
                        <select 
                            className="form-input"
                            value={selectedDs} 
                            onChange={e => setSelectedDs(e.target.value)}
                            style={{ width: "100%", height: 48, background: "var(--surface)" }}
                        >
                            <option value="">-- Choose Salesman --</option>
                            {dsList.map(ds => (
                                <option key={ds.id} value={ds.dsId || ds.id}>
                                    {ds.name} ({ds.dsId || ds.id})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ width: 220 }}>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>
                            AUDIT DATE
                        </label>
                        <input 
                            type="date" 
                            className="form-input"
                            value={selectedDate} 
                            onChange={e => setSelectedDate(e.target.value)}
                            style={{ width: "100%", height: 48, background: "var(--surface)" }}
                        />
                    </div>

                    <button 
                        onClick={fetchTravelData} 
                        disabled={loading || !selectedDs}
                        className="btn-primary"
                        style={{ height: 48, padding: "0 32px", display: "flex", alignItems: "center", gap: 10 }}
                    >
                        {loading ? "INITIALIZING..." : <><FaRoute /> REGENERATE LOGS</>}
                    </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 32 }}>
                    {/* TIMELINE COLUMN */}
                    <div className="premium-card" style={{ padding: 40 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 40 }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Deployment Timeline</h3>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700 }}>{travelLog.length} VISITS CAPTURED</div>
                        </div>

                        {travelLog.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "80px 0", color: "var(--text-muted)" }}>
                                <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📍</div>
                                <div>No operational data found for the selected criteria.</div>
                            </div>
                        ) : (
                            <div style={{ position: "relative", paddingLeft: 32 }}>
                                {/* VERTICAL LINE */}
                                <div style={{ position: "absolute", left: 0, top: 10, bottom: 10, width: 2, background: "var(--border)", opacity: 0.5 }}></div>

                                {travelLog.map((log, idx) => (
                                    <div key={log.id} style={{ position: "relative", marginBottom: 48, animation: `fadeInUp 0.3s ease-out ${idx * 0.05}s both` }}>
                                        {/* DOT */}
                                        <div style={{ 
                                            position: "absolute", 
                                            left: -37, 
                                            top: 4, 
                                            width: 12, 
                                            height: 12, 
                                            borderRadius: "50%", 
                                            background: "var(--primary)",
                                            boxShadow: "0 0 0 4px var(--surface), 0 0 0 7px rgba(255, 107, 0, 0.1)"
                                        }}></div>

                                        <div style={{ display: "flex", gap: 24 }}>
                                            <div style={{ minWidth: 60, fontSize: 13, fontWeight: 800, color: "var(--text-muted)", paddingTop: 2 }}>
                                                {log.time}
                                            </div>
                                            <div style={{ flex: 1, padding: 24, borderRadius: 16, background: "var(--surface-hover)", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div>
                                                    <div style={{ fontWeight: 800, fontSize: 16, color: "var(--text-main)", marginBottom: 4 }}>{log.outletName}</div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)" }}>ID: {log.outletId}</span>
                                                        <div style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--border)" }}></div>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: log.lat ? "var(--success)" : "var(--error)", fontSize: 11, fontWeight: 700 }}>
                                                            <FaMapMarkerAlt size={10} /> {log.lat ? `VERIFIED: ${log.lat.toFixed(4)}, ${log.long.toFixed(4)}` : "GPS NULL ERROR"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: "right" }}>
                                                    <div style={{ fontSize: 18, fontWeight: 900, color: "var(--primary)" }}>₹{log.amount.toLocaleString()}</div>
                                                    {log.lat && (
                                                        <a 
                                                            href={`https://www.google.com/maps/search/?api=1&query=${log.lat},${log.long}`} 
                                                            target="_blank" 
                                                            rel="noreferrer"
                                                            style={{ 
                                                              display: "inline-flex", 
                                                              marginTop: 10, 
                                                              fontSize: 10, 
                                                              fontWeight: 800, 
                                                              color: "var(--text-main)", 
                                                              textDecoration: "none",
                                                              background: "var(--surface)",
                                                              padding: "4px 10px",
                                                              borderRadius: 6,
                                                              border: "1px solid var(--border)",
                                                              textTransform: "uppercase"
                                                            }}
                                                        >
                                                            Inspect Location
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* SUMMARY WIDGET */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                        <div className="premium-card" style={{ padding: 24 }}>
                            <h4 style={{ margin: "0 0 20px 0", fontSize: 14, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase" }}>Performance Pulse</h4>
                            
                            <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>TOTAL HUB VISITS</div>
                                <div style={{ fontSize: 32, fontWeight: 900 }}>{travelLog.length}</div>
                            </div>

                            <div style={{ marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>CUMULATIVE REVENUE</div>
                                <div style={{ fontSize: 32, fontWeight: 900, color: "var(--success)" }}>₹{travelLog.reduce((sum, l) => sum + l.amount, 0).toLocaleString()}</div>
                            </div>
                        </div>

                        <div className="premium-card" style={{ padding: 24, background: "rgba(255, 107, 0, 0.03)", border: "1px dashed var(--primary)" }}>
                            <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                                <FaExclamationTriangle color="var(--primary)" />
                                <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800 }}>System Integrity</h4>
                            </div>
                            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
                                Pathing is derived from transactional timestamps. Outlets with "GPS NULL" indicate location services were disabled during order submission.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </Layout>
    );
}

